/**
 * File uploads, from `POST /api/v1/files/`.
 *
 * One call, `multipart/form-data`, carrying the bytes as `file` and what they
 * are for as `purpose`. The backend stores it against the caller's workspace
 * and answers with the id and URL the file can be referred to by afterwards -
 * so a form holds a `public_id`, never the bytes.
 *
 * The multipart body is a `FormData`, which `api.post` sends as-is: the browser
 * sets the content type and its boundary, and `client.ts` deliberately leaves
 * that header alone.
 *
 * Like the rest of the API this needs a bearer token and a completed workspace
 * onboarding; without one the backend answers 403, which arrives as an
 * `ApiError` carrying its own message.
 */

import { API_BASE_URL, api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  FilePurpose,
  StoredFile,
  StoredFileResponse,
} from "../types/brand.types";

/**
 * Where a stored file can actually be fetched from.
 *
 * The backend answers with a path rooted at its own origin, which is not the
 * one the app is served from - so a `src` built from it has to carry the API's
 * origin with it. Resolved rather than concatenated, because the path arrives
 * in more than one shape: `/media/…` and `media/…` both have to land on the
 * API, and a URL that is already absolute has to be left alone. Concatenating
 * only the first of those quietly resolves the second against the page it is
 * rendered on - `…/my-brand/media/…`, which is a 404 and a broken image.
 */
export const mediaUrl = (url: string): string => {
  if (!url) return "";

  try {
    // The trailing slash matters: without it the last path segment of the base
    // would be dropped when resolving a relative path against it.
    return new URL(url, `${API_BASE_URL}/`).toString();
  } catch {
    return url;
  }
};

/** A stored file in the app's own casing. */
export const toStoredFile = (response: StoredFileResponse): StoredFile => ({
  publicId: response.public_id,
  url: response.url,
  contentType: response.content_type,
  byteSize: response.byte_size,
});

/**
 * Longer than the shared timeout, because this is the one call whose body goes
 * up rather than down - and a home connection's upstream is its slow half. A
 * megabyte, the ceiling the brand form enforces, still fits comfortably.
 */
export const UPLOAD_TIMEOUT_MS = 60000;

export interface UploadFileOptions {
  signal?: AbortSignal;
}

/**
 * Uploads one file and returns what the backend now knows it as.
 *
 * `purpose` is the caller's to get right - it decides where the file is filed,
 * not merely how it is labelled.
 */
export async function uploadFile(
  getToken: GetToken,
  file: File,
  purpose: FilePurpose,
  { signal }: UploadFileOptions = {}
): Promise<StoredFile> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot upload a file.");
  }

  const body = new FormData();
  body.append("file", file);
  body.append("purpose", purpose);

  const response = await api.post<StoredFileResponse>(
    API_ENDPOINTS.files.upload,
    body,
    { token, signal, timeoutMs: UPLOAD_TIMEOUT_MS }
  );

  return toStoredFile(response);
}
